import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Container,
    MenuItem,
    Grid,
    AppBar,
    Toolbar,
    IconButton,
    InputAdornment,
    Paper,
    Tabs,
    Tab,
    Divider,
    CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    CalendarMonth,
    Person,
    Business,
    Badge,
    FileDownload,
    ArrowBack,
    Groups,
    DateRange,
    AccessTime,
    Timer,
    TimerOff
} from '@mui/icons-material';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
    boxShadow: theme.shadows[3],
    borderRadius: theme.shape.borderRadius * 2,
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:hover': {
        boxShadow: theme.shadows[6],
        transform: 'translateY(-4px)'
    }
}));

const ReportButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1.5),
    fontWeight: 500,
    textTransform: 'none',
    borderRadius: theme.shape.borderRadius * 1.5
}));

const ReportScreen = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [formValues, setFormValues] = useState({
        start: '',
        end: '',
        employee: '',
        department: '',
        designation: '',
        reportType: 'standard' // Added for report type selection
    });

    // Data states
    const [employees, setEmployees] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [shifts, setShifts] = useState([]); // Added shifts state

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('token');
            const userID = localStorage.getItem('userID');
            const headers = {
                'Authorization': `Bearer ${token}`
            };

            // Fetch all required data in parallel
            const [employeesRes, departmentsRes, designationsRes, companiesRes, shiftsRes] = await Promise.all([
                fetch(`http://biometick.com/get-employees/${userID}`, { headers }),
                fetch(`http://biometick.com/get-departments/${userID}`, { headers }),
                fetch(`http://biometick.com/get-designation/${userID}`, { headers }),
                fetch(`http://biometick.com/get-company/${userID}`, { headers }),
                fetch(`http://biometick.com/all-shift-plan/${userID}`, { headers }) // Added shifts endpoint
            ]);

            const employeesData = await employeesRes.json();
            const departmentsData = await departmentsRes.json();
            const designationsData = await designationsRes.json();
            const companiesData = await companiesRes.json();
            const shiftsData = await shiftsRes.json();

            setEmployees(employeesData.length > 0 ? employeesData : []);
            setDepartments(departmentsData.length > 0 ? departmentsData : []);
            setDesignations(designationsData.length > 0 ? designationsData : []);
            setCompanies(companiesData.length > 0 ? companiesData : []);
            setShifts(shiftsData.length > 0 ? shiftsData : []);
        };

        fetchData();
    }, []);

    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const downloadMusterRoll = async () => {
        setLoading(true);
        try {
            if (companies.length > 0 && employees.length > 0) {
                const response = await fetch("http://biometick.com/get-all-attendance/" + localStorage.getItem("userID"), {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                const data = await response.json();
                const finalData = formatAttendanceData(data);
                downloadCSV(finalData, "muster_roll");
            }
        } catch (error) {
            console.error('Error downloading muster roll:', error);
        } finally {
            setLoading(false);
        }
    };

    // Function to calculate time difference in hours
    const calculateHoursDifference = (startTime, endTime) => {
        if (!startTime || !endTime) return 0;

        const start = new Date(startTime);
        const end = new Date(endTime);

        // Calculate difference in milliseconds
        const diffMs = end - start;
        // Convert to hours (milliseconds to hours)
        return diffMs / (1000 * 60 * 60);
    };

    // Function to get shift information for an employee
    const getEmployeeShift = (employeeId) => {
        const employee = employees.find(emp => emp._id === employeeId);
        if (!employee) return null;

        return shifts.find(shift => shift._id === employee.shift);
    };

    // Function to get expected work hours for a day based on shift
    const getExpectedHours = (shift, attendance) => {
        if (!shift) return 8; // Default to 8 hours if no shift found


        const date = new Date(attendance);
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });

        if (shift.shiftType === "Fixed") {
            const daySchedule = shift.fixedShift[dayOfWeek];
            if (!daySchedule || !daySchedule.checkIn || !daySchedule.checkOut) return 0;

            // Parse time strings (assuming format like "09:00")
            const [inHours, inMinutes] = daySchedule.checkIn.split(':').map(Number);
            const [outHours, outMinutes] = daySchedule.checkOut.split(':').map(Number);

            // Calculate hours difference
            let hoursDiff = outHours - inHours;
            const minutesDiff = outMinutes - inMinutes;
            hoursDiff += minutesDiff / 60;

            // Handle overnight shifts
            if (hoursDiff < 0) hoursDiff += 24;

            return hoursDiff;
        } else if (shift.shiftType === "Flexible") {
            const dayDuration = shift.flexibleShift[dayOfWeek];
            return dayDuration ? dayDuration.duration : 0;
        }

        return 8; // Default fallback
    };

    // Function to check if an employee is late
    const isLate = (attendance, employeeId) => {
        const shift = getEmployeeShift(employeeId);
        if (!shift || shift.shiftType !== "Fixed") return false;

        const date = new Date(attendance.entryTime);
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });

        const daySchedule = shift.fixedShift[dayOfWeek];
        if (!daySchedule || !daySchedule.checkIn) return false;

        // Parse expected check-in time
        const [expectedHours, expectedMinutes] = daySchedule.checkIn.split(':').map(Number);

        // Create expected check-in datetime
        const expectedCheckIn = new Date(date);
        expectedCheckIn.setHours(expectedHours, expectedMinutes, 0, 0);

        // Calculate minutes late
        const actualCheckIn = new Date(attendance.entryTime);
        const minutesLate = (actualCheckIn - expectedCheckIn) / (1000 * 60);

        // Consider late if more than 5 minutes past expected time
        return minutesLate > 5;
    };

    // Function to calculate late minutes
    const calculateLateMinutes = (attendance, employeeId) => {
        const shift = getEmployeeShift(employeeId);
        if (!shift || shift.shiftType !== "Fixed") return 0;

        const date = new Date(attendance.entryTime);
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });

        const daySchedule = shift.fixedShift[dayOfWeek];
        if (!daySchedule || !daySchedule.checkIn) return 0;

        // Parse expected check-in time
        const [expectedHours, expectedMinutes] = daySchedule.checkIn.split(':').map(Number);

        // Create expected check-in datetime
        const expectedCheckIn = new Date(date);
        expectedCheckIn.setHours(expectedHours, expectedMinutes, 0, 0);

        // Calculate minutes late
        const actualCheckIn = new Date(attendance.entryTime);
        const minutesLate = (actualCheckIn - expectedCheckIn) / (1000 * 60);

        return minutesLate > 0 ? minutesLate : 0;
    };

    // Function to calculate overtime
    const calculateOvertime = (attendance, employeeId) => {
        const shift = getEmployeeShift(employeeId);
        if (!shift) return 0;
        console.log(attendance);
        console.log(employeeId);
        console.log(shift);
        const date = new Date(attendance.entryTime);
        console.log(date);
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });

        console.log(dayOfWeek); // e.g., "saturday"


        let expectedHours = 0;

        if (shift.shiftType === "Fixed") {
            const daySchedule = shift.fixedShift[dayOfWeek];
            if (!daySchedule || !daySchedule.checkIn || !daySchedule.checkOut) return 0;

            // Parse expected times
            const [inHours, inMinutes] = daySchedule.checkIn.split(':').map(Number);
            const [outHours, outMinutes] = daySchedule.checkOut.split(':').map(Number);

            // Calculate expected hours
            let expected = outHours - inHours + (outMinutes - inMinutes) / 60;
            if (expected < 0) expected += 24; // Handle overnight shifts

            expectedHours = expected;
        } else if (shift.shiftType === "Flexible") {
            const dayDuration = shift.flexibleShift[dayOfWeek];
            expectedHours = dayDuration ? dayDuration.duration : 0;
        }

        if (expectedHours === 0) return 0;

        // Calculate actual hours worked
        const actualHours = calculateHoursDifference(attendance.entryTime, attendance.exitTime);

        // Return overtime hours (if positive)
        return actualHours > expectedHours ? (actualHours - expectedHours) : 0;
    };

    const generateReport = async () => {
        setLoading(true);
        try {
            const { start, end, reportType } = formValues;
            if (!start || !end) {
                alert('Please select both start and end dates');
                return;
            }

            // Prepare request headers and body
            const myHeaders = new Headers();
            myHeaders.append("Authorization", `Bearer ${localStorage.getItem("token")}`);
            myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

            const urlencoded = new URLSearchParams();
            urlencoded.append("startDate", new Date(start).toISOString());
            urlencoded.append("endDate", new Date(end).toISOString());

            const requestOptions = {
                method: 'POST',
                headers: myHeaders,
                body: urlencoded,
                redirect: 'follow'
            };

            // Fetch attendance data
            const attendanceResponse = await fetch("http://biometick.com/get-attendance", requestOptions);
            const attendanceText = await attendanceResponse.text();
            let attendanceData = JSON.parse(attendanceText);

            // Filter based on current tab
            switch (activeTab) {
                case 1: // Employee
                    if (formValues.employee) {
                        attendanceData = attendanceData.filter(item => item.employee === formValues.employee);
                    }
                    break;
                case 2: // Department
                    if (formValues.department) {
                        attendanceData = attendanceData.filter(item => {
                            const employee = employees.find(emp => emp._id === item.employee);
                            return employee && employee.department === formValues.department;
                        });
                    }
                    break;
                case 3: // Designation
                    if (formValues.designation) {
                        attendanceData = attendanceData.filter(item => {
                            const employee = employees.find(emp => emp._id === item.employee);
                            return employee && employee.designation === formValues.designation;
                        });
                    }
                    break;
                default:
                    // No additional filtering for Date tab
                    break;
            }

            // Process the data based on report type
            let finalData;
            const reportName = ['date', 'employee', 'department', 'designation'][activeTab];
            let fileName;

            switch (reportType) {
                case 'monthly-hours':
                    finalData = processMonthlyHoursReport(attendanceData);
                    fileName = `monthly_hours_by_${reportName}`;
                    break;
                case 'overtime':
                    finalData = processOvertimeReport(attendanceData);
                    fileName = `overtime_by_${reportName}`;
                    break;
                case 'late-hours':
                    finalData = processLateHoursReport(attendanceData);
                    fileName = `late_hours_by_${reportName}`;
                    break;
                default:
                    finalData = formatAttendanceData(attendanceData);
                    fileName = `attendance_by_${reportName}`;
                    break;
            }

            downloadCSV(finalData, fileName);
        } catch (error) {
            console.error('Error generating report:', error);
        } finally {
            setLoading(false);
        }
    };

    // Process monthly hours data
    const processMonthlyHoursReport = (attendanceData) => {
        // Group by employee
        const employeeAttendance = {};

        // Process each attendance record
        attendanceData.forEach(record => {
            const employeeId = record.employee;
            const employee = employees.find(emp => emp._id === employeeId);
            if (!employee) return;

            // Initialize employee data if not exists
            if (!employeeAttendance[employeeId]) {
                employeeAttendance[employeeId] = {
                    name: employee.name,
                    totalActualHours: 0,
                    totalExpectedHours: 0,
                    company: companies.find(c => c._id === employee.company)?.name || 'Unknown',
                    department: departments.find(d => d._id === employee.department)?.name || 'Unknown',
                    designation: designations.find(d => d._id === employee.designation)?.name || 'Unknown'
                };
            }

            // Calculate hours for this record
            const actualHours = calculateHoursDifference(record.entryTime, record.exitTime);
            const expectedHours = getExpectedHours(getEmployeeShift(employeeId), record.entryTime);

            // Add to employee totals
            employeeAttendance[employeeId].totalActualHours += actualHours;
            employeeAttendance[employeeId].totalExpectedHours += expectedHours;
        });

        // Convert to array format for CSV
        return Object.values(employeeAttendance).map(data => ({
            'Employee': data.name,
            'Company': data.company,
            'Department': data.department,
            'Designation': data.designation,
            'Total Actual Hours': data.totalActualHours.toFixed(2),
            'Total Expected Hours': data.totalExpectedHours.toFixed(2),
            'Percentage Completion': ((data.totalActualHours / data.totalExpectedHours) * 100).toFixed(2) + '%'
        }));
    };

    // Process overtime data
    const processOvertimeReport = (attendanceData) => {
        // Group by employee
        const employeeOvertimes = {};

        // Process each attendance record
        attendanceData.forEach(record => {
            const employeeId = record.employee;
            const employee = employees.find(emp => emp._id === employeeId);
            if (!employee) return;

            // Initialize employee data if not exists
            if (!employeeOvertimes[employeeId]) {
                employeeOvertimes[employeeId] = {
                    name: employee.name,
                    totalOvertimeHours: 0,
                    overtimeDays: 0,
                    company: companies.find(c => c._id === employee.company)?.name || 'Unknown',
                    department: departments.find(d => d._id === employee.department)?.name || 'Unknown',
                    designation: designations.find(d => d._id === employee.designation)?.name || 'Unknown'
                };
            }

            // Calculate overtime for this record
            const overtimeHours = calculateOvertime(record, employeeId);

            // Add to employee totals
            if (overtimeHours > 0) {
                employeeOvertimes[employeeId].totalOvertimeHours += overtimeHours;
                employeeOvertimes[employeeId].overtimeDays += 1;
            }
        });

        // Convert to array format for CSV
        return Object.values(employeeOvertimes).map(data => ({
            'Employee': data.name,
            'Company': data.company,
            'Department': data.department,
            'Designation': data.designation,
            'Total Overtime Hours': data.totalOvertimeHours.toFixed(2),
            'Overtime Days': data.overtimeDays
        }));
    };

    // Process late hours data
    const processLateHoursReport = (attendanceData) => {
        // Group by employee
        const employeeLates = {};

        // Process each attendance record
        attendanceData.forEach(record => {
            const employeeId = record.employee;
            const employee = employees.find(emp => emp._id === employeeId);
            if (!employee) return;

            // Initialize employee data if not exists
            if (!employeeLates[employeeId]) {
                employeeLates[employeeId] = {
                    name: employee.name,
                    totalLateMinutes: 0,
                    lateDays: 0,
                    company: companies.find(c => c._id === employee.company)?.name || 'Unknown',
                    department: departments.find(d => d._id === employee.department)?.name || 'Unknown',
                    designation: designations.find(d => d._id === employee.designation)?.name || 'Unknown'
                };
            }

            // Check if employee was late
            if (isLate(record, employeeId)) {
                const lateMinutes = calculateLateMinutes(record, employeeId);
                employeeLates[employeeId].totalLateMinutes += lateMinutes;
                employeeLates[employeeId].lateDays += 1;
            }
        });

        // Convert to array format for CSV
        return Object.values(employeeLates).map(data => ({
            'Employee': data.name,
            'Company': data.company,
            'Department': data.department,
            'Designation': data.designation,
            'Total Late Minutes': data.totalLateMinutes.toFixed(0),
            'Total Late Hours': (data.totalLateMinutes / 60).toFixed(2),
            'Late Days': data.lateDays
        }));
    };

    const formatAttendanceData = (data) => {
        return data.map(element => ({
            'Employee': employees.find(employee => employee._id === element.employee)?.name || 'Unknown',
            'Company': companies.find(company => {
                const emp = employees.find(employee => employee._id === element.employee);
                return emp && company._id === emp.company;
            })?.name || 'Unknown',
            'Entry Time': new Date(element.entryTime).toString().split(' GMT')[0],
            'Exit Time': new Date(element.exitTime).toString().split(' GMT')[0],
            'Hours Worked': calculateHoursDifference(element.entryTime, element.exitTime).toFixed(2)
        }));
    };

    const downloadCSV = (data, filename) => {
        const csvData = convertToCSV(data);
        const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = `${filename}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    const convertToCSV = (json) => {
        if (!json.length) return '';

        const rows = [];
        const headerRow = Object.keys(json[0]);
        rows.push(headerRow.join(","));

        for (const row of json) {
            const values = headerRow.map(header => {
                const val = row[header] || '';
                // Escape quotes and handle commas in CSV
                return `"${String(val).replace(/"/g, '""')}"`;
            });
            rows.push(values.join(","));
        }

        return rows.join("\n");
    };

    // Render date selection fields (common to all tabs)
    const renderDateFields = () => (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    variant="outlined"
                    type="date"
                    label="Start Date"
                    name="start"
                    value={formValues.start}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <DateRange color="primary" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    variant="outlined"
                    type="date"
                    label="End Date"
                    name="end"
                    value={formValues.end}
                    onChange={handleFormChange}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <DateRange color="primary" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Grid>
        </Grid>
    );

    // Render report type selection
    const renderReportTypeSelection = () => (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
                <TextField
                    select
                    fullWidth
                    variant="outlined"
                    label="Report Type"
                    name="reportType"
                    value={formValues.reportType}
                    onChange={handleFormChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <CalendarMonth color="primary" />
                            </InputAdornment>
                        ),
                    }}
                >
                    <MenuItem value="standard">Standard Attendance</MenuItem>
                    <MenuItem value="monthly-hours">Monthly Hours Analysis</MenuItem>
                    <MenuItem value="overtime">Overtime Analysis</MenuItem>
                    <MenuItem value="late-hours">Late Hours Analysis</MenuItem>
                </TextField>
            </Grid>
        </Grid>
    );

    // Tab-specific fields
    const renderTabSpecificFields = () => {
        switch (activeTab) {
            case 1: // Employee
                return (
                    <Grid item xs={12}>
                        <TextField
                            select
                            fullWidth
                            variant="outlined"
                            label="Select Employee"
                            name="employee"
                            value={formValues.employee}
                            onChange={handleFormChange}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person color="primary" />
                                    </InputAdornment>
                                ),
                            }}
                        >
                            {employees.map((option) => (
                                <MenuItem key={option._id} value={option._id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                );

            case 2: // Department
                return (
                    <Grid item xs={12}>
                        <TextField
                            select
                            fullWidth
                            variant="outlined"
                            label="Select Department"
                            name="department"
                            value={formValues.department}
                            onChange={handleFormChange}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Business color="primary" />
                                    </InputAdornment>
                                ),
                            }}
                        >
                            {departments.map((option) => (
                                <MenuItem key={option._id} value={option._id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                );

            case 3: // Designation
                return (
                    <Grid item xs={12}>
                        <TextField
                            select
                            fullWidth
                            variant="outlined"
                            label="Select Designation"
                            name="designation"
                            value={formValues.designation}
                            onChange={handleFormChange}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Badge color="primary" />
                                    </InputAdornment>
                                ),
                            }}
                        >
                            {designations.map((option) => (
                                <MenuItem key={option._id} value={option._id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                );

            default:
                return null;
        }
    };

    // Render the main content of the application
    return (
        <Container maxWidth="md">
            <AppBar position="static" color="primary" elevation={0} sx={{ borderRadius: '16px 16px 0 0', mb: 0 }}>
                <Toolbar>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 500 }}>
                        Attendance Reports
                    </Typography>
                </Toolbar>
            </AppBar>

            <Paper elevation={3} sx={{ borderRadius: '0 0 16px 16px', overflow: 'hidden' }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    textColor="primary"
                    indicatorColor="primary"
                    aria-label="report tabs"
                >
                    <Tab icon={<CalendarMonth />} label="By Date" iconPosition="start" />
                    <Tab icon={<Person />} label="By Employee" iconPosition="start" />
                    <Tab icon={<Groups />} label="By Department" iconPosition="start" />
                    <Tab icon={<Badge />} label="By Designation" iconPosition="start" />
                </Tabs>

                <Divider />

                <Box sx={{ p: 3 }}>
                    {/* Tab-specific fields */}
                    <Grid container spacing={2}>
                        {renderTabSpecificFields()}
                    </Grid>

                    {/* Date range selector (common to all tabs) */}
                    {renderDateFields()}

                    {/* Report type selection */}
                    {renderReportTypeSelection()}

                    {/* Action buttons */}
                    <Grid container spacing={2} sx={{ mt: 3 }}>
                        <Grid item xs={12} sm={6}>
                            <ReportButton
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={generateReport}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <FileDownload />}
                            >
                                {loading ? 'Generating...' : 'Generate Report'}
                            </ReportButton>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <ReportButton
                                variant="outlined"
                                color="primary"
                                fullWidth
                                onClick={downloadMusterRoll}
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <FileDownload />}
                            >
                                {loading ? 'Processing...' : 'Download Muster Roll'}
                            </ReportButton>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            <Typography variant="caption" color="textSecondary" align="center" sx={{ mt: 2, display: 'block' }}>
                Reports are generated in CSV format and will download automatically
            </Typography>
        </Container>
    );
};

export default ReportScreen;